import { motion } from "framer-motion";
import {
    ShieldAlert,
    FileCheck2,
    ClipboardCheck,
    Activity,
    WifiOff,
    BarChart3,
    Workflow,
    ArrowUpRight,
} from "lucide-react";
import { LANDING } from "@/constants/testIds";

const IMG_PLATFORM =
    "https://images.pexels.com/photos/3192662/pexels-photo-3192662.jpeg?auto=compress&cs=tinysrgb&w=1200";
const IMG_ANALYTICS =
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";
const IMG_CONTAINERS =
    "https://images.unsplash.com/photo-1606964212858-c215029db704?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
};

const Card = ({ testid, className = "", children, delay = 0 }) => (
    <motion.div
        {...fadeUp}
        transition={{ duration: 0.55, delay, ease: "easeOut" }}
        data-testid={testid}
        className={`group relative overflow-hidden border border-[#091524]/10 rounded-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(9,21,36,0.12)] ${className}`}
    >
        {children}
    </motion.div>
);

export const Features = () => {
    return (
        <section id="features" className="relative bg-white py-24 md:py-32">
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                {/* Header */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20">
                    <div className="md:col-span-4">
                        <span className="label-eyebrow text-[#0052CC]">
                            // Platform Capabilities
                        </span>
                    </div>
                    <div className="md:col-span-8">
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#091524] tracking-tight leading-[1.05]">
                            One platform for every case,
                            <br />
                            <span className="text-[#091524]/40">every action, every site.</span>
                        </h2>
                        <p className="mt-6 text-lg text-[#475569] max-w-2xl leading-relaxed">
                            Purpose-built for enterprises with complex multi-site safety
                            operations. Automated workflows, real-time visibility, and data-driven
                            insights — from field to boardroom.
                        </p>
                    </div>
                </div>

                {/* Bento grid */}
                <div className="grid grid-cols-1 md:grid-cols-6 grid-rows-none md:auto-rows-[minmax(220px,auto)] gap-6">
                    {/* Row 1: Safety (wide, dark) */}
                    <Card
                        testid={LANDING.featureSafety}
                        className="md:col-span-4 bg-[#091524] text-white p-8 md:p-10"
                        delay={0}
                    >
                        <div className="flex items-start justify-between gap-4 h-full">
                            <div className="flex flex-col justify-between h-full">
                                <ShieldAlert size={32} className="text-[#0052CC]" strokeWidth={2} />
                                <div>
                                    <div className="label-eyebrow text-white/50 mb-3">
                                        Case Management · 01
                                    </div>
                                    <h3 className="font-display text-2xl md:text-3xl font-bold leading-tight">
                                        Safety Case Management
                                    </h3>
                                    <p className="mt-3 text-white/70 max-w-md">
                                        Log incidents, near-misses and observations with full
                                        evidence upload. Assign owners, set severity, and track
                                        every action through to closure.
                                    </p>
                                </div>
                            </div>
                            <ArrowUpRight
                                size={22}
                                className="text-white/40 group-hover:text-[#0052CC] group-hover:rotate-12 transition-all duration-300"
                            />
                        </div>
                    </Card>

                    {/* Assurance (image) */}
                    <Card
                        testid={LANDING.featureAssurance}
                        className="md:col-span-2 relative min-h-[280px]"
                        delay={0.08}
                    >
                        <img
                            src={IMG_PLATFORM}
                            alt="Offshore platform"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#091524] via-[#091524]/60 to-[#091524]/20" />
                        <div className="relative z-10 p-8 h-full flex flex-col justify-between text-white">
                            <FileCheck2 size={28} className="text-white" strokeWidth={2} />
                            <div>
                                <div className="label-eyebrow text-white/60 mb-2">
                                    Case Management · 02
                                </div>
                                <h3 className="font-display text-xl md:text-2xl font-bold leading-tight">
                                    Assurance Case Management
                                </h3>
                            </div>
                        </div>
                    </Card>

                    {/* Row 2: Audit */}
                    <Card
                        testid={LANDING.featureAudit}
                        className="md:col-span-2 bg-[#F0F4F8] p-8"
                        delay={0.12}
                    >
                        <div className="flex flex-col justify-between h-full">
                            <ClipboardCheck size={28} className="text-[#0052CC]" strokeWidth={2} />
                            <div>
                                <div className="label-eyebrow text-[#0052CC] mb-2">
                                    Case Management · 03
                                </div>
                                <h3 className="font-display text-xl md:text-2xl font-bold text-[#091524]">
                                    Audit Case Management
                                </h3>
                                <p className="mt-2 text-[#475569] text-sm">
                                    Structured audits with sub-category workflows, evidence, and
                                    corrective action tracking.
                                </p>
                            </div>
                        </div>
                    </Card>

                    {/* Action Tracker (wide) */}
                    <Card
                        testid={LANDING.featureActionTracker}
                        className="md:col-span-4 bg-white p-8 md:p-10"
                        delay={0.16}
                    >
                        <div className="flex flex-col md:flex-row gap-8 h-full">
                            <div className="flex-1">
                                <Activity size={32} className="text-[#0052CC]" strokeWidth={2} />
                                <div className="label-eyebrow text-[#091524]/50 mt-6 mb-2">
                                    Action Tracker
                                </div>
                                <h3 className="font-display text-2xl md:text-3xl font-bold text-[#091524] leading-tight">
                                    Every action, tracked. Every owner, accountable.
                                </h3>
                                <p className="mt-3 text-[#475569]">
                                    Assign corrective actions, monitor progress, and record
                                    close-out details across every case category — in a single
                                    live dashboard.
                                </p>
                            </div>
                            {/* Mock dashboard */}
                            <div className="flex-1 border border-[#091524]/10 rounded-sm p-4 bg-[#F8FAFC]">
                                {[
                                    { label: "Open", value: "24", tone: "bg-[#0052CC]" },
                                    { label: "In Progress", value: "12", tone: "bg-[#091524]" },
                                    { label: "Overdue", value: "3", tone: "bg-red-500" },
                                    { label: "Closed", value: "148", tone: "bg-emerald-600" },
                                ].map((row) => (
                                    <div
                                        key={row.label}
                                        className="flex items-center justify-between py-2.5 border-b last:border-b-0 border-[#091524]/10"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className={`w-2 h-2 rounded-full ${row.tone}`} />
                                            <span className="text-sm text-[#091524]/80 font-medium">
                                                {row.label}
                                            </span>
                                        </div>
                                        <span className="font-display font-bold text-[#091524]">
                                            {row.value}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>

                    {/* Row 3: Offline */}
                    <Card
                        testid={LANDING.featureOffline}
                        className="md:col-span-2 bg-[#091524] text-white p-8"
                        delay={0.2}
                    >
                        <div className="flex flex-col justify-between h-full">
                            <WifiOff size={28} className="text-[#0052CC]" strokeWidth={2} />
                            <div>
                                <div className="label-eyebrow text-white/50 mb-2">
                                    Offline-First
                                </div>
                                <h3 className="font-display text-xl md:text-2xl font-bold">
                                    Works where the signal doesn&apos;t
                                </h3>
                                <p className="mt-2 text-white/70 text-sm">
                                    Log cases offshore, at depth, or in the field. Automatic
                                    sync on reconnect.
                                </p>
                            </div>
                        </div>
                    </Card>

                    {/* Benchmarking (image) */}
                    <Card
                        testid={LANDING.featureBenchmarking}
                        className="md:col-span-2 relative min-h-[260px]"
                        delay={0.24}
                    >
                        <img
                            src={IMG_ANALYTICS}
                            alt="Analytics"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#091524] via-[#091524]/50 to-transparent" />
                        <div className="relative z-10 p-8 h-full flex flex-col justify-between text-white">
                            <BarChart3 size={28} className="text-white" strokeWidth={2} />
                            <div>
                                <div className="label-eyebrow text-white/60 mb-2">
                                    Benchmarking
                                </div>
                                <h3 className="font-display text-xl md:text-2xl font-bold">
                                    Compare performance across sites
                                </h3>
                            </div>
                        </div>
                    </Card>

                    {/* Workflows */}
                    <Card
                        testid={LANDING.featureWorkflows}
                        className="md:col-span-2 bg-[#F0F4F8] p-8"
                        delay={0.28}
                    >
                        <div className="flex flex-col justify-between h-full">
                            <Workflow size={28} className="text-[#0052CC]" strokeWidth={2} />
                            <div>
                                <div className="label-eyebrow text-[#0052CC] mb-2">
                                    Automated Workflows
                                </div>
                                <h3 className="font-display text-xl md:text-2xl font-bold text-[#091524]">
                                    Rules-based routing
                                </h3>
                                <p className="mt-2 text-[#475569] text-sm">
                                    Auto-assign cases, escalate overdue actions, and notify
                                    stakeholders in real time.
                                </p>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Bottom band */}
                <div className="mt-20 border-t border-[#091524]/10 pt-10 grid grid-cols-2 md:grid-cols-4 gap-8">
                    {[
                        ["Real-Time", "Visibility"],
                        ["Configurable", "To Your Ops"],
                        ["Secure", "Cloud + Offline"],
                        ["Multi-Site", "Benchmarking"],
                    ].map(([a, b]) => (
                        <div key={a}>
                            <div className="font-display font-bold text-[#091524] text-lg">
                                {a}
                            </div>
                            <div className="text-sm text-[#475569] mt-1">{b}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
